<template>
    <div 
        class="x-log-viewer" 
        ref="logContainer"
        :style="{ height: typeof height === 'number' ? height + 'px' : height }"
    >
        <!-- 支持字符串格式的日志显示 -->
        <div v-if="logString" class="log-string-content" v-html="formattedLogString"></div>
        
        <!-- 支持数组格式的日志显示 -->
        <div v-else>
            <div v-for="(log, index) in logs" class="log-item" :key="index">
                <div class="line-number" v-if="hasNumber">{{ index + 1 }}</div>
                <div class="content" :style="getLogStyle(log)">{{ log.content }}</div>
            </div>
        </div>

        <!-- 动画效果-->
        <div class="log-item" v-if="loading">
            <div class="line-number" v-if="hasNumber">{{ logLines.length + 1 }}</div>
            <div class="log-loading">
                <div class="loading-dots">
                    <span class="dot">•</span>
                    <span class="dot">•</span>
                    <span class="dot">•</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'XLogViewer',
    props: {
        // 支持字符串格式的日志
        log: {
            type: String,
            default: ''
        },
        // 支持数组格式的日志（向后兼容）
        logs: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        autoScroll: {
            type: Boolean,
            default: true
        },
        hasNumber: {
            type: Boolean,
            default: true
        },
        // 日志高度
        height: {
            type: [String, Number],
            default: '400px' // 设置默认高度
        }
    },
    data() {
        return {
            // 移除重复的logs定义，使用props中的logs
        }
    },
    computed: {
        // 判断是否使用字符串格式的日志
        logString() {
            return this.log && this.log.trim() !== '';
        },
        // 将字符串日志按行分割
        logLines() {
            if (this.logString) {
                return this.log.split('\n').filter(line => line.trim() !== '');
            }
            return this.logs;
        },
        // 格式化字符串日志，添加行号和样式
        formattedLogString() {
            if (!this.logString) return '';
            
            const lines = this.log.split('\n');
            return lines.map((line, index) => {
                const lineNumber = this.hasNumber ? `<span class="line-number">${index + 1}</span>` : '';
                const content = this.escapeHtml(line);
                
                // 检测日志类型并添加样式
                let logClass = '';
                
                if (line.includes('✓')) {
                    logClass = 'log-success';
                } else if (line.includes('✗')) {
                    logClass = 'log-error';
                } else if (line.includes('⚠')) {
                    logClass = 'log-warn';
                }
                
                return `<div class="log-line ${logClass}">${lineNumber}<span class="line-content">${content}</span></div>`;
            }).join('');
        }
    },

    methods: {
        _formatTime(date) {
            let hours = date.getHours().toString().padStart(2, '0');
            let minutes = date.getMinutes().toString().padStart(2, '0');
            let seconds = date.getSeconds().toString().padStart(2, '0');
            return `${hours}:${minutes}:${seconds}`;
        },
        _scrollToEnd() {
            if (!this.$refs.logContainer || !this.autoScroll) {
                return
            }
            this.$nextTick(() => {
                this.$refs.logContainer.scrollTop = this.$refs.logContainer.scrollHeight;
            });
        },
        // HTML转义，防止XSS攻击
        escapeHtml(text) {
            const div = document.createElement('div');
            div.textContent = text;
            return div.innerHTML;
        },
        // 获取日志内联样式
        getLogStyle(log) {
            return { 
                color: log.color || '#e0e0e0' // 默认柔和的白色
            };
        },
        // 向后兼容的方法
        info(content) {
            if (this.logString) {
                // 字符串模式下，通过emit通知父组件更新
                this.$emit('update:log', this.log + content + '\n');
            } else {
                this.logs.push({
                    color: '#e0e0e0', // 柔和的白色默认文字颜色，降低对比度
                    content
                })
            }
        },
        success(content) {
            if (this.logString) {
                this.$emit('update:log', this.log + content + '\n');
            } else {
                this.logs.push({
                    color: '#4CAF50', // 更柔和的绿色
                    content: `✓ ${content}`, // 添加成功图标
                    type: 'success'
                })
            }
        },
        error(content) {
            if (this.logString) {
                this.$emit('update:log', this.log + content + '\n');
            } else {
                this.logs.push({
                    color: '#F44336', // 更柔和的红色
                    content: `✗ ${content}`, // 添加错误图标
                    type: 'error'
                })
            }
        },
        warn(content) {
            if (this.logString) {
                this.$emit('update:log', this.log + content + '\n');
            } else {
                this.logs.push({
                    color: '#FF9800', // 橙色警告
                    content: `⚠ ${content}`, // 添加警告图标
                    type: 'warn'
                })
            }
        },
        clearLog() {
            if (this.logString) {
                this.$emit('update:log', '');
            } else {
                this.$emit('clear-logs');
            }
        }
    },
    watch: {
        // 监听日志变化，自动滚动到底部
        log: {
            handler() {
                this._scrollToEnd();
            },
            immediate: true
        },
        logs: {
            handler() {
                this._scrollToEnd();
            },
            immediate: true,
            deep: true
        }
    },
    updated() {
        this._scrollToEnd(); // 每次更新后滚动到底部
    },
    mounted() {
        this._scrollToEnd(); // 组件挂载后滚动到底部
    }
}
</script>

<style scoped lang="scss">
.x-log-viewer {
    width: 100%;
    min-height: 200px; /* 最小高度 */
    overflow-y: auto; /* 超出部分可滚动 */
    overflow-x: auto;
    font-size: 12px;
    background-color: #222 !important; /* 强制背景色 */
    border: 1px solid #444; /* 添加边框 */
    border-radius: 4px; /* 添加圆角 */
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15); /* 添加阴影 */
    padding: 8px;
    font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
    box-sizing: border-box; /* 确保padding包含在高度内 */

    // 字符串格式日志样式
    .log-string-content {
        // 使用深度选择器确保动态内容也能应用样式
        ::v-deep .log-line {
            display: flex;
            align-items: baseline;
            margin-bottom: 5px;
            color: #e0e0e0; // 降低对比度，更柔和的白色
            line-height: 20px;
            min-height: 20px;
            white-space: pre-wrap; // 支持换行和自动换行
            word-wrap: break-word; // 长单词自动换行
            word-break: break-all; // 强制换行
            box-sizing: border-box;
            padding-left: 0; // 移除左边距，因为组件本身有padding

            .line-number {
                font-size: 12px;
                min-width: 24px;
                text-align: right;
                color: #888888; // 降低对比度，更柔和的颜色
                padding-right: 8px;
                -webkit-user-select: none;
                -moz-user-select: none;
                -ms-user-select: none;
                user-select: none;
                flex-shrink: 0; // 防止行号被压缩
                font-weight: 500; // 稍微加粗，提高可读性
            }

            .line-content {
                font-size: 12px;
                padding-right: 10px;
                overflow-wrap: break-word;
                white-space: pre-wrap; // 保持换行和空格
                word-wrap: break-word;
                flex: 1; // 占据剩余空间
                color: #e0e0e0; // 降低对比度，更柔和的白色
            }
            
            // 字符串格式日志类型样式
            &.log-success {
                .line-content {
                    color: #4CAF50;
                }
            }
            
            &.log-error {
                .line-content {
                    color: #F44336;
                }
            }
            
            &.log-warn {
                .line-content {
                    color: #FF9800;
                }
            }
        }
    }

    // 数组格式日志样式（向后兼容）
    .log-item {
        display: flex;
        align-items: baseline;
        margin-bottom: 5px;
        color: #e0e0e0; // 降低对比度，更柔和的白色
        line-height: 20px;
        min-height: 20px;
        white-space: pre-wrap; // 支持换行和自动换行
        word-wrap: break-word;
        word-break: break-all;
        box-sizing: border-box;
        padding-left: 0; // 移除左边距，因为组件本身有padding

        .line-number {
            font-size: 12px;
            min-width: 24px;
            text-align: right;
            color: #888888; // 降低对比度，更柔和的颜色
            padding-right: 8px;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
            user-select: none;
            flex-shrink: 0;
            font-weight: 500; // 稍微加粗，提高可读性
        }

        .time {
            margin-right: 20px;
            font-size: 12px;
            color: #bbbbbb; // 更亮的时间戳颜色
        }

        .content {
            font-size: 12px;
            padding-right: 10px;
            overflow-wrap: break-word;
            white-space: pre-wrap; // 支持换行和自动换行
            word-wrap: break-word;
            flex: 1;
            color: #e0e0e0; // 降低对比度，更柔和的白色（默认颜色，会被内联样式覆盖）
            border-radius: 2px;
            padding: 2px 4px;
            margin: 1px 0;
        }
    }

    // 加载动画样式
    .log-loading {
        font-size: 12px;
        padding-right: 10px;
        overflow-wrap: break-word;
        white-space: pre-wrap;
        word-wrap: break-word;
        flex: 1;
        color: #e0e0e0;
        border-radius: 2px;
        padding: 2px 4px;
        margin: 1px 0;
        
        .loading-dots {
            .dot {
                animation: blink 0.6s infinite both;
                margin-left: -1px;

                &:first-child {
                    margin-left: 0;
                }

                &:nth-child(2) {
                    animation-delay: 0.2s;
                }

                &:nth-child(3) {
                    animation-delay: 0.4s;
                }
            }
        }
    }
}

@keyframes blink {
    0% {
        opacity: 0.2;
    }

    20% {
        opacity: 1;
    }

    100% {
        opacity: 0.2;
    }
}
</style>
